import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ChevronLeft, Volume2, CheckCircle } from 'lucide-react';
import { usePitchMatch, useUpdateLab } from '../../hooks/useLabAPI';
import audioService from '../../services/audioService';

const PitchMatch = ({ t }) => {
  const navigate = useNavigate();
  const { savePitchMatch, saving } = usePitchMatch();
  const { updateLab } = useUpdateLab();

  const [step, setStep] = useState(1); // 1 = broad scan, 2 = fine tuning
  const [frequency, setFrequency] = useState(7800);
  const [trials, setTrials] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [avgFt, setAvgFt] = useState(null);
  const [toast, setToast] = useState(null);

  // Step 1 ranges
  const step1Min = 1000;
  const step1Max = 12000;

  // Step 2 ranges (±100 Hz around detected)
  const [step2Center, setStep2Center] = useState(7800);
  const step2Range = 100;

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handlePlayTone = () => {
    if (isPlaying) {
      audioService.stopTone();
      setIsPlaying(false);
    } else {
      audioService.playTone(frequency, 0.3); // Play at 30% volume
      setIsPlaying(true);
    }
  };

  const handleFrequencyChange = (value) => {
    setFrequency(value[0]);
    if (isPlaying) {
      audioService.stopTone();
      audioService.playTone(value[0], 0.3);
    }
  };

  const handleNextToStep2 = () => {
    audioService.stopTone();
    setIsPlaying(false);
    setStep2Center(frequency);
    setFrequency(frequency);
    setStep(2);
  };

  const handleRecordTrial = () => {
    const newTrials = [...trials, frequency];
    setTrials(newTrials);

    if (newTrials.length === 3) {
      // Calculate average
      const avg = Math.round(newTrials.reduce((a, b) => a + b, 0) / newTrials.length);
      setAvgFt(avg);
    } else {
      showToast(`Essai ${newTrials.length}/3 enregistré`);
    }
  };

  const handleSaveAsFt = async () => {
    if (!avgFt) return;

    try {
      // Save pitch match
      const result = await savePitchMatch({
        f_t: avgFt,
        method: 'broad_scan',
        confidence: 8
      });

      // Update active program with new f_t
      await updateLab({
        programs: {
          notch: {
            f_t: avgFt
          }
        }
      });

      showToast(t.labToastSaved);
      
      setTimeout(() => {
        navigate('/settings/lab');
      }, 2000);
    } catch (error) {
      showToast(t.labToastError, 'error');
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 pt-20 px-8 pb-40">
      <div className="max-w-2xl mx-auto">
        {/* Back button */}
        <button
          onClick={() => {
            audioService.stopTone();
            navigate(-1);
          }}
          className="mb-4 p-2 text-white hover:bg-gray-800 rounded-lg transition-colors"
        >
          <ChevronLeft size={24} />
        </button>

        <h1 className="text-3xl font-bold text-white mb-6">{t.pitchTitle}</h1>

        {/* Toast */}
        {toast && (
          <Alert className={`mb-4 ${toast.type === 'error' ? 'border-red-300 bg-red-50' : 'border-green-300 bg-green-50'}`}>
            <AlertDescription className={toast.type === 'error' ? 'text-red-800' : 'text-green-800'}>
              {toast.message}
            </AlertDescription>
          </Alert>
        )}

        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">
              {step === 1 ? t.pitchStep1 : t.pitchStep2}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Frequency Display */}
            <div className="text-center">
              <div className="text-5xl font-bold text-purple-400 mb-2">
                {frequency} Hz
              </div>
              {avgFt && (
                <div className="text-sm text-gray-300">
                  {t.pitchAvgFt}: <span className="font-bold text-green-400">{avgFt} Hz</span>
                </div>
              )}
            </div>

            {/* Frequency Slider */}
            <div className="px-4">
              <label className="block text-sm font-medium text-gray-200 mb-2">
                {t.labFieldsFt}
              </label>
              <input
                type="range"
                value={frequency}
                onChange={(e) => handleFrequencyChange([parseInt(e.target.value)])}
                min={step === 1 ? step1Min : step2Center - step2Range}
                max={step === 1 ? step1Max : step2Center + step2Range}
                step={step === 1 ? 50 : 10}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-2">
                <span>{step === 1 ? step1Min : step2Center - step2Range} Hz</span>
                <span>{step === 1 ? step1Max : step2Center + step2Range} Hz</span>
              </div>
            </div>

            {/* Play/Stop Button */}
            <Button
              onClick={handlePlayTone}
              className={`w-full py-4 text-lg font-semibold ${
                isPlaying
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-purple-600 hover:bg-purple-700'
              }`}
            >
              <Volume2 className="mr-2" size={20} />
              {isPlaying ? 'Arrêter' : 'Jouer le son'}
            </Button>

            {/* Step Actions */}
            {step === 1 ? (
              <Button
                onClick={handleNextToStep2}
                className="w-full bg-green-600 hover:bg-green-700"
              >
                <CheckCircle className="mr-2" size={20} />
                {t.pitchLikeMyTinnitus}
              </Button>
            ) : (
              <div className="space-y-3">
                {trials.length < 3 ? (
                  <Button
                    onClick={handleRecordTrial}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    Enregistrer l'essai {trials.length + 1}/3
                  </Button>
                ) : (
                  <Button
                    onClick={handleSaveAsFt}
                    disabled={saving}
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="mr-2" size={20} />
                    {saving ? t.loading : t.pitchSaveAsFt}
                  </Button>
                )}

                {/* Trials indicator */}
                <div className="flex gap-2 justify-center">
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className={`w-3 h-3 rounded-full ${
                        i <= trials.length ? 'bg-green-500' : 'bg-gray-600'
                      }`}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Instructions */}
            <Alert className="bg-gray-700 border-gray-600">
              <AlertDescription className="text-gray-200 text-sm">
                {step === 1
                  ? 'Ajustez le curseur jusqu\'à ce que le son ressemble à votre acouphène, puis cliquez sur "Ça ressemble à mon acouphène".'
                  : 'Affinez en enregistrant 3 essais. La moyenne sera calculée automatiquement pour plus de précision.'}
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PitchMatch;
